/**
 * Typed API client for the Sudoku Worker.
 *
 * All requests go through `apiFetch`, which:
 *  - Prepends the base URL in production (empty in dev — Vite proxies /api)
 *  - Sends cookies (credentials: "include")
 *  - Throws ApiError on non-2xx responses
 */
export class ApiError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = "ApiError";
    }
}
async function apiFetch(path, init = {}) {
    const res = await fetch(`/api${path}`, {
        ...init,
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...(init.headers ?? {}),
        },
    });
    if (!res.ok) {
        let message = res.statusText;
        try {
            const body = (await res.json());
            if (body.error)
                message = body.error;
        }
        catch {
            // ignore parse error
        }
        throw new ApiError(res.status, message);
    }
    return res.json();
}
export async function fetchMe() {
    try {
        const data = await apiFetch("/auth/me");
        return data.user;
    }
    catch (err) {
        if (err instanceof ApiError && err.status === 401)
            return null;
        throw err;
    }
}
export function getOAuthUrl(provider) {
    return `/api/auth/${provider}`;
}
export async function signOut() {
    await apiFetch("/auth/signout", { method: "POST" });
}
export async function submitScore(payload) {
    return apiFetch("/scores", {
        method: "POST",
        body: JSON.stringify(payload),
    });
}
export async function fetchLeaderboard(opts) {
    const params = new URLSearchParams({
        difficulty: opts.difficulty,
        limit: String(opts.limit ?? 25),
        offset: String(opts.offset ?? 0),
    });
    if (opts.daily)
        params.set("daily", opts.daily);
    if (opts.puzzleId)
        params.set("puzzleId", opts.puzzleId);
    return apiFetch(`/scores/leaderboard?${params}`);
}
export async function saveGame(payload) {
    await apiFetch("/games", { method: "PUT", body: JSON.stringify(payload) });
}
export async function loadGame(difficulty) {
    try {
        const data = await apiFetch(`/games/${difficulty}`);
        return data.game;
    }
    catch (err) {
        if (err instanceof ApiError && err.status === 404)
            return null;
        throw err;
    }
}
export async function deleteGame(difficulty) {
    await apiFetch(`/games/${difficulty}`, { method: "DELETE" });
}
export async function fetchUserStats() {
    return apiFetch("/users/me/stats");
}
export async function fetchUserRank(difficulty) {
    return apiFetch(`/users/me/rank?difficulty=${difficulty}`);
}
export async function fetchDailyPuzzle(difficulty, date) {
    const params = new URLSearchParams({ difficulty });
    if (date)
        params.set("date", date);
    return apiFetch(`/puzzles/daily?${params}`);
}
